import React, { useState, useEffect, useRef } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { LivenessService } from '../services/LivenessService';
import { EnhancedAttendanceService } from '../services/EnhancedAttendanceService';
import { locationService } from '../services/LocationService';
import './TestLivenessScreen.css';
import '@aws-amplify/ui-react-liveness/styles.css';

const TestLivenessScreen: React.FC = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [punchCode, setPunchCode] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'input' | 'camera' | 'liveness' | 'complete'>('input');
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Custom credential provider that gets credentials from your backend
  // const getCredentialsFromBackend = async () => {
  //   try {
  //     console.log('🔑 Getting AWS credentials from backend...');
      
  //     const response = await fetch(`${LivenessService.API_BASE_URL}/liveness/credentials`, {
  //       method: 'GET',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Accept': 'application/json',
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Failed to get credentials: ${response.status}`);
  //     }

  //     const credentials = await response.json();
  //     console.log('✅ Got credentials from backend');
      
  //     return {
  //       accessKeyId: credentials.accessKeyId,
  //       secretAccessKey: credentials.secretAccessKey,
  //       sessionToken: credentials.sessionToken,
  //       expiration: credentials.expiration
  //     };
  //   } catch (error) {
  //     console.error('❌ Failed to get credentials from backend:', error);
  //     throw error;
  //   }
  // };

  // Get user location on component mount
  useEffect(() => {
    const getCurrentLocation = async () => {
      try {
        const position = await locationService.getCurrentLocation();
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      } catch (error) {
        console.error('Failed to get location:', error);
        setError('Location access is required for attendance marking');
      }
    };

    getCurrentLocation();

    // Cleanup camera stream on unmount
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const createLivenessSession = async (): Promise<string> => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('Creating liveness session...');
      const session = await LivenessService.createSession();
      
      console.log('Session created:', session);
      return session.sessionId;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create liveness session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartLiveness = async () => {
    if (!punchCode.trim()) {
      setError('Please enter your punch code');
      return;
    }

    if (!location) {
      setError('Location is required. Please allow location access and try again.');
      return;
    }

    try {
      // Step 1: Start camera for face detection option
      setCurrentStep('camera');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to start process');
    }
  };

  const startCamera = async () => {
    try {
      console.log('Starting camera for face detection...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        } 
      });
      console.log('Camera stream obtained:', stream);
      setCameraStream(stream);
      setShowCamera(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded, playing...');
          videoRef.current?.play().catch(e => console.error('Error playing video:', e));
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const captureImageFromVideo = (): string | null => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Canvas context not available');
      return null;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64 image
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Extract base64 data (remove data:image/jpeg;base64, prefix)
    const base64Data = imageDataUrl.split(',')[1];
    
    console.log('Image captured, base64 length:', base64Data.length);
    return base64Data;
  };

  const handleFaceDetection = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('🎯 Starting face detection attendance marking...');
      
      // Capture image from video
      const imageBase64 = captureImageFromVideo();
      
      if (!imageBase64) {
        throw new Error('Failed to capture image from camera');
      }

      console.log('📸 Image captured, marking attendance with face detection...');

      // Mark attendance with face detection
      const attendanceResult = await EnhancedAttendanceService.markEntry(
        punchCode.trim(),
        location!.latitude,
        location!.longitude,
        imageBase64
      );

      console.log('✅ Face detection attendance result:', attendanceResult);

      if (attendanceResult.success) {
        let successMessage = `Attendance marked successfully! ${attendanceResult.message}`;
        
        if (attendanceResult.faceDetectionResult) {
          const faceResult = attendanceResult.faceDetectionResult;
          successMessage += `\n\nFace Detection: ${faceResult.faces.length} face(s) detected`;
          
          if (faceResult.faces.length > 0) {
            const verifiedFaces = faceResult.faces.filter((face: any) => face.verified);
            if (verifiedFaces.length > 0) {
              successMessage += `\nVerified faces: ${verifiedFaces.length}`;
            }
          }
        }

        setSuccess(successMessage);
        setCurrentStep('complete');
        stopCamera();
      } else {
        throw new Error(attendanceResult.message);
      }
    } catch (error) {
      console.error('❌ Error in face detection attendance:', error);
      setError(error instanceof Error ? error.message : 'Failed to complete face detection attendance');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseLiveness = async () => {
    try {
      // Step 1: Create liveness session
      const newSessionId = await createLivenessSession();
      setSessionId(newSessionId);
      setCurrentStep('liveness');
      stopCamera();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to start liveness detection');
    }
  };

  const handleLivenessSuccess = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('✅ Liveness detection completed successfully!');
      console.log('📹 Video selfie was captured and processed by AWS FaceLivenessDetector');
      console.log('🔄 Now verifying liveness results and marking attendance...');
      
      // The FaceLivenessDetector has already called StartFaceLivenessSession with the video
      // Now we verify the results using GetFaceLivenessSessionResults
      const verificationResult = await LivenessService.verifyLiveness(sessionId);
      
      console.log('🔍 Liveness verification result:', verificationResult);
      
      if (!verificationResult.success) {
        throw new Error(verificationResult.message);
      }

      // Mark attendance after successful liveness verification
      if (!location) {
        throw new Error('Location is required for attendance marking');
      }

      console.log('📍 Marking attendance with location:', location);
      
      const attendanceResult = await EnhancedAttendanceService.markEntry(
        punchCode.trim(),
        location.latitude,
        location.longitude
      );

      console.log('✅ Attendance result:', attendanceResult);

      if (attendanceResult.success) {
        setSuccess(`Attendance marked successfully! ${attendanceResult.message}`);
        setCurrentStep('complete');
      } else {
        throw new Error(attendanceResult.message);
      }
    } catch (error) {
      console.error('❌ Error in liveness success handler:', error);
      setError(error instanceof Error ? error.message : 'Failed to complete attendance marking');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLivenessError = (error: any) => {
    console.error('❌ Liveness detection error:', error);
    console.log('📹 Video capture or processing failed');
    setError('Liveness detection failed. Please try again.');
    setCurrentStep('input');
  };

  const resetFlow = () => {
    setSessionId('');
    setPunchCode('');
    setCurrentStep('input');
    setError('');
    setSuccess('');
    stopCamera();
  };

  return (
    <div className="test-liveness-screen">
      <div className="container">
        <h1>Test Liveness Attendance</h1>
        
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="success-message">
            <p>{success}</p>
          </div>
        )}

        {currentStep === 'input' && (
          <div className="input-section">
            <div className="form-group">
              <label htmlFor="punchCode">Punch Code:</label>
              <input
                type="text"
                id="punchCode"
                value={punchCode}
                onChange={(e) => setPunchCode(e.target.value)}
                placeholder="Enter your punch code"
                disabled={isLoading}
              />
            </div>

            <div className="location-info">
              {location ? (
                <p className="location-success">
                  ✓ Location detected: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
              ) : (
                <p className="location-pending">📍 Getting location...</p>
              )}
            </div>

            <button
              onClick={handleStartLiveness}
              disabled={isLoading || !punchCode.trim() || !location}
              className="start-button"
            >
              {isLoading ? 'Starting...' : 'Choose Attendance Method'}
            </button>
          </div>
        )}

        {currentStep === 'camera' && (
          <div className="camera-section">
            <h2>Choose Attendance Method</h2>
            <p>You can mark attendance using either face detection or AWS liveness verification.</p>
            
            <div className="method-selection">
              <div className="method-option">
                <h3>📸 Face Detection</h3>
                <p>Quick face detection using your camera</p>
                
                {!showCamera ? (
                  <button onClick={startCamera} className="method-button face-detection">
                    Start Camera for Face Detection
                  </button>
                ) : (
                  <div className="camera-container">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{ width: '100%', maxWidth: '400px', height: 'auto', borderRadius: '8px' }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    
                    <div className="camera-controls">
                      <button 
                        onClick={handleFaceDetection} 
                        disabled={isLoading}
                        className="capture-button"
                      >
                        {isLoading ? 'Processing...' : '📸 Capture & Mark Attendance'}
                      </button>
                      <button onClick={stopCamera} className="stop-camera-button">
                        Stop Camera
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="method-divider">OR</div>

              <div className="method-option">
                <h3>🔒 AWS Liveness Detection</h3>
                <p>Advanced liveness verification with AWS Rekognition</p>
                <button onClick={handleUseLiveness} className="method-button liveness">
                  Use AWS Liveness Detection
                </button>
              </div>
            </div>

            <button onClick={() => setCurrentStep('input')} className="back-button">
              ← Back to Input
            </button>
          </div>
        )}

        {currentStep === 'liveness' && sessionId && (
          <div className="liveness-section">
            <h2>Face Liveness Detection</h2>
            <p>Session ID: <code>{sessionId}</code></p>
            <p>📹 Position your face in the camera and follow the instructions. The system will capture a short selfie video.</p>
            
            <div className="liveness-detector-container">
              {/* {awsCredentials ? ( */}
                <FaceLivenessDetector
                  sessionId={sessionId}
                  region="ap-south-1"
                  onAnalysisComplete={handleLivenessSuccess}
                  onError={handleLivenessError}
                />
              {/* ) : (
                <div className="credentials-loading">
                  <p>🔑 Loading AWS credentials...</p>
                </div>
              )} */}
            </div>
            
            {isLoading && (
              <div className="processing-overlay">
                <div className="processing-content">
                  <div className="spinner"></div>
                  <p>🔄 Processing liveness verification and marking attendance...</p>
                  <p className="processing-details">
                    ✅ Video captured<br/>
                    🔍 Verifying with AWS Rekognition<br/>
                    📝 Marking attendance...
                  </p>
                </div>
              </div>
            )}

            <div className="liveness-info">
              <h3>What's happening:</h3>
              <div className="info-steps">
                <div className="info-step">
                  <span className="step-number">1</span>
                  <span>📹 Camera captures your selfie video</span>
                </div>
                <div className="info-step">
                  <span className="step-number">2</span>
                  <span>🚀 Video sent to AWS StartFaceLivenessSession</span>
                </div>
                <div className="info-step">
                  <span className="step-number">3</span>
                  <span>🔍 AWS analyzes liveness and returns confidence score</span>
                </div>
                <div className="info-step">
                  <span className="step-number">4</span>
                  <span>✅ Attendance marked if liveness verified</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'complete' && (
          <div className="complete-section">
            <h2>✓ Attendance Marked Successfully!</h2>
            <p>Your attendance has been recorded with liveness verification.</p>
            
            <div className="session-info">
              <p><strong>Session ID:</strong> {sessionId}</p>
              <p><strong>Punch Code:</strong> {punchCode}</p>
              {location && (
                <p><strong>Location:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
              )}
            </div>

            <button onClick={resetFlow} className="reset-button">
              Mark Another Attendance
            </button>
          </div>
        )}

        <div className="navigation">
          <button onClick={() => window.history.back()} className="back-button">
            ← Back to Main
          </button>
        </div>

        <div className="integration-info">
          <h3>🔧 Integration Status & Debugging:</h3>
          <div className="debug-info">
            <div className="debug-section">
              <h4>📋 Current Session:</h4>
              <p><strong>Session ID:</strong> {sessionId || 'Not created yet'}</p>
              <p><strong>Current Step:</strong> {currentStep}</p>
              <p><strong>Location:</strong> {location ? `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}` : 'Not available'}</p>
            </div>
            
            <div className="debug-section">
              <h4>🔄 API Flow (No Cognito):</h4>
              <div className="api-flow">
                {/* <div className="api-step">
                  <strong>1. Get AWS Credentials:</strong> GET /liveness/credentials
                  <br/><small>🔑 Backend provides temporary AWS credentials using STS</small>
                </div> */}
                <div className="api-step">
                  <strong>2. CreateFaceLivenessSession:</strong> POST /liveness/session
                  <br/><small>✅ Creates AWS session and returns session_id</small>
                </div>
                <div className="api-step">
                  <strong>3. StartFaceLivenessSession:</strong> Handled by FaceLivenessDetector
                  <br/><small>📹 Captures selfie video and sends to AWS automatically</small>
                </div>
                <div className="api-step">
                  <strong>4. GetFaceLivenessSessionResults:</strong> POST /liveness/verify
                  <br/><small>🔍 Retrieves confidence score and verification results</small>
                </div>
                <div className="api-step">
                  <strong>5. Mark Attendance:</strong> POST /attendance/mark
                  <br/><small>✅ Records attendance with liveness verification</small>
                </div>
              </div>
            </div>

            <div className="debug-section">
              <h4>🎥 Video Capture Status:</h4>
              <p>The AWS FaceLivenessDetector component automatically:</p>
              <ul>
                <li>📷 Accesses your camera</li>
                <li>🎬 Records a short selfie video</li>
                <li>🚀 Sends video to AWS StartFaceLivenessSession API</li>
                <li>🔍 AWS analyzes the video for liveness</li>
                <li>✅ Calls onAnalysisComplete when done</li>
              </ul>
            </div>

            <div className="debug-section">
              <h4>🛠️ Backend Requirements (No Cognito):</h4>
              <p>Your backend needs to implement:</p>
              <ul>
                {/* <li><code>GET /liveness/credentials</code> - Provide temporary AWS credentials</li> */}
                <li><code>POST /liveness/session</code> - Call AWS CreateFaceLivenessSession</li>
                <li><code>POST /liveness/verify</code> - Call AWS GetFaceLivenessSessionResults</li>
                <li><code>POST /attendance/mark</code> - Mark attendance after verification</li>
              </ul>
              <p><strong>✅ No Cognito setup required!</strong> Your backend handles all AWS authentication.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestLivenessScreen;